import { useState, useRef } from "react";
import { Box } from "@mui/material";
import { FormikProps } from "formik";
import "../../styles/scss/Form.scss";

interface FileUploadProps {
  name: string;
  formik: FormikProps<any>;
  label?: string;
  accept?: string;
  editImagePreview?: string | null;
  multiple?: boolean;
}

export default function FileUpload({
  formik,
  name,
  label = "Upload Image",
  accept = "image/*",
  editImagePreview,
  multiple = false,
}: FileUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [removedExisting, setRemovedExisting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const error = formik.touched[name] && Boolean(formik.errors[name]);
  const helperText = formik.touched[name] && formik.errors[name];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (multiple) {
        const newPreviews: string[] = [];
        const fileArray: File[] = [];

        Array.from(files).forEach((file) => {
          newPreviews.push(URL.createObjectURL(file));
          fileArray.push(file);
        });

        setPreviews((prev) => [...prev, ...newPreviews]);
        const existingFiles = formik.values[name] || [];
        formik.setFieldValue(name, [...existingFiles, ...fileArray]);
      } else {
        const file = files[0];
        formik.setFieldValue(name, file);
        setPreview(URL.createObjectURL(file));
        setRemovedExisting(false);
      }
    }
  };

  const handleRemove = (index?: number) => {
    if (multiple && index !== undefined) {
      setPreviews((prev) => prev.filter((_, i) => i !== index));
      const currentFiles = [...(formik.values[name] || [])];
      currentFiles.splice(index, 1);
      formik.setFieldValue(name, currentFiles);
    } else {
      formik.setFieldValue(name, null);
      setPreview(null);
      if (editImagePreview) {
        setRemovedExisting(true);
      }
    }
  };

  const handleSetFeatured = (index: number) => {
    const currentFiles = [...(formik.values[name] || [])];
    const featured = currentFiles.splice(index, 1)[0];
    currentFiles.unshift(featured);
    formik.setFieldValue(name, currentFiles);

    const newPreviews = [...previews];
    const featuredPreview = newPreviews.splice(index, 1)[0];
    newPreviews.unshift(featuredPreview);
    setPreviews(newPreviews);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        Array.from(files).forEach((file) => dataTransfer.items.add(file));
        fileInputRef.current.files = dataTransfer.files;
        handleFileChange({ target: fileInputRef.current } as any);
      }
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const displayImage = preview || (!removedExisting && editImagePreview);

  return (
    <Box className="multi-image-upload">
      <Box className="image-upload-area">
        <Box
          className={`image-upload-zone ${isDragActive ? "drag-active" : ""}`}
          onClick={handleBrowse}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            multiple={multiple}
            style={{ display: "none" }}
          />

          <Box className="upload-zone-icon">
            <span className="material-symbols-outlined">cloud_upload</span>
          </Box>

          <Box className="upload-zone-text">
            <p>Drag & drop or <span className="upload-link">browse</span></p>
            <p>JPG, PNG up to 1MB</p>
          </Box>
        </Box>

        {displayImage && (
          <Box className="image-preview-container">
            <Box className="image-preview-single">
              <img
                src={displayImage}
                alt="Preview"
                className="preview-image"
              />
              <Box className="preview-actions">
                <button
                  className="remove-btn"
                  onClick={() => handleRemove()}
                  title="Remove"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Box className="form-error" style={{ marginTop: "8px" }}>
          <span className="material-symbols-outlined">error</span>
          {helperText as string}
        </Box>
      )}
    </Box>
  );
}
